'use client';

import { useParams } from 'next/navigation';
import { motion } from 'framer-motion';
import ScrollReveal from '@/app/components/animations/ScrollReveal';
import PixelSquares from '@/app/components/ui/PixelSquares';
import { TbEye as Eye, TbKey as Key, TbMessage as MessageSquare, TbShieldCheck as ShieldCheck, TbUserCheck as UserCheck } from 'react-icons/tb';

const content = {
    en: {
        eyebrow: 'Privacy Protection',
        title: 'Privacy Policy',
        subtitle: 'At nibras Agency, we value your trust. This Privacy Policy details how we collect, protect, and responsibly use your personal information.',
        lastUpdated: 'Last Updated: May 2026',
        sections: [
            {
                icon: ShieldCheck,
                title: '1. Introduction & Scope',
                body: 'This Privacy Policy applies to all services offered by nibras Digital Marketing Agency (referred to as "we", "us", or "our") through our website nibras.agency and related interaction platforms. By utilizing our forms or services, you consent to the processing policies detailed here.',
            },
            {
                icon: Eye,
                title: '2. Information We Collect',
                body: 'We collect information directly from you when you submit our contact forms, inquire about services, or communicate with our representatives. This includes your name, company name, email address, phone number, and any project notes or message details you provide.',
            },
            {
                icon: Key,
                title: '3. Data Security & Storage',
                body: 'We prioritize your security. We implement robust server defenses and industry-standard encryption protocols (SSL/TLS) to shield your personal details from unauthorized access, breach, alteration, or disclosure. Database records are stored securely with verified enterprise-grade cloud providers.',
            },
            {
                icon: UserCheck,
                title: '4. How We Use Your Data',
                body: 'Your information is used strictly to process service inquiries, respond to quick replies, deliver requested proposals, and optimize the overall performance of our platforms. We do not sell, rent, or lease your personal information to third parties.',
            },
            {
                icon: MessageSquare,
                title: '5. Inquiries & Contact',
                body: 'If you have any questions regarding this Privacy Policy, your rights under data laws, or how we manage your information, feel free to email our privacy operations desk directly at info@nibras.agency.',
            },
        ],
    },
    ar: {
        eyebrow: 'حماية الخصوصية',
        title: 'سياسة الخصوصية',
        subtitle: 'في وكالة نبراس، نقدر ثقتكم بنا. توضح سياسة الخصوصية هذه كيفية جمع بياناتكم الشخصية وحمايتها واستخدامها بمسؤولية.',
        lastUpdated: 'آخر تحديث: مايو 2026',
        sections: [
            {
                icon: ShieldCheck,
                title: '1. المقدمة والنطاق',
                body: 'تنطبق سياسة الخصوصية هذه على جميع الخدمات التي تقدمها وكالة نبراس للتسويق الرقمي (المشار إليها بـ "الوكالة" أو "نحن") عبر موقعنا الإلكتروني nibras.agency ومنصات التفاعل ذات الصلة. باستخدامك لنماذجنا أو خدماتنا، فإنك توافق على سياسات المعالجة المفصلة هنا.',
            },
            {
                icon: Eye,
                title: '2. البيانات التي نجمعها',
                body: 'نحن نجمع البيانات مباشرة منك عندما تقوم بتقديم نماذج التواصل الخاصة بنا، أو الاستفسار عن الخدمات، أو التواصل مع ممثلينا. ويشمل ذلك اسمك، واسم الشركة، وعنوان البريد الإلكتروني، ورقم الهاتف، وأي ملاحظات مشروع أو تفاصيل رسائل تقدمها.',
            },
            {
                icon: Key,
                title: '3. أمن وتخزين البيانات',
                body: 'نحن نضع أمنك على رأس أولوياتنا. نحن نطبق دفاعات برمجية قوية وبروتوكولات تشفير معيارية (SSL/TLS) لحماية بياناتك الشخصية من الوصول غير المصرح به، أو الاختراق، أو التعديل، أو الإفصاح. يتم تخزين سجلات قواعد البيانات بأمان لدى مزودي سحابة معتمدين.',
            },
            {
                icon: UserCheck,
                title: '4. كيف نستخدم بياناتك',
                body: 'تُستخدم بياناتك بشكل صارم لمعالجة طلبات الخدمات، والرد على الاستفسارات، وتقديم المقترحات المطلوبة، وتحسين الأداء العام لمنصاتنا. نحن لا نبيع أو نؤجر أو نشارك معلوماتك الشخصية مع أطراف ثالثة.',
            },
            {
                icon: MessageSquare,
                title: '5. الاستفسارات والتواصل',
                body: 'إذا كانت لديك أي أسئلة تتعلق بسياسة الخصوصية هذه، أو حقوقك بموجب قوانين حماية البيانات، أو كيفية إدارتنا لمعلوماتك، فلا تتردد في مراسلة مكتب عمليات الخصوصية لدينا مباشرة عبر البريد الإلكتروني info@nibras.agency.',
            },
        ],
    },
};

export default function PrivacyPage() {
    const params = useParams();
    const locale = (params?.locale as 'en' | 'ar') || 'ar';
    const isAr = locale === 'ar';
    const t = content[locale];

    return (
        <main 
            className="min-h-screen relative overflow-hidden py-32 px-6 bg-brand-bg text-brand-text" 
            dir={isAr ? 'rtl' : 'ltr'}
        >
            {/* Elegant Background Gradients & Elements */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,131,186,0.15),transparent_35rem),radial-gradient(circle_at_70%_80%,rgba(234,50,40,0.1),transparent_35rem)]" />
            
            <PixelSquares
                pattern="frame"
                tone="blue"
                className="-right-10 top-24"
                opacityClassName="opacity-20"
                sizeClassName="w-40 md:w-72"
            />
            
            <PixelSquares
                pattern="corner"
                tone="red"
                className="bottom-16 left-4 md:left-14"
                opacityClassName="opacity-[0.16]"
                sizeClassName="w-28 md:w-44"
            />

            <div className="max-w-4xl mx-auto relative z-10">
                {/* Header Section */}
                <div className="text-center mb-20">
                    <ScrollReveal mode="fade-up">
                        <p className="mb-5 text-sm font-bold uppercase tracking-[0.22em] text-brand-cta">
                            {t.eyebrow}
                        </p>
                        <h1 className="text-5xl md:text-7xl font-heading font-bold mb-8">
                            {t.title}
                        </h1>
                    </ScrollReveal>
                    
                    <ScrollReveal mode="fade-up" delay={0.2}>
                        <p className="mx-auto max-w-2xl font-body text-lg md:text-xl leading-relaxed opacity-70 mb-4">
                            {t.subtitle}
                        </p>
                        <span className="inline-block px-4 py-1.5 rounded-full bg-brand-text/5 border border-brand-text/10 text-xs font-semibold opacity-60 font-body">
                            {t.lastUpdated}
                        </span>
                    </ScrollReveal>
                </div>

                {/* Privacy Content Blocks with glassmorphic cards */}
                <div className="space-y-8">
                    {t.sections.map((section, idx) => {
                        const IconComponent = section.icon;
                        return (
                            <ScrollReveal 
                                key={idx} 
                                mode="fade-up" 
                                delay={0.1 * idx}
                                className="p-8 md:p-10 bg-brand-text/[0.03] border border-brand-text/10 rounded-3xl backdrop-blur-md hover:border-brand-text/20 transition-all duration-300 group shadow-lg"
                            >
                                <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
                                    <div className="p-4 rounded-2xl bg-brand-cta/10 text-brand-cta group-hover:bg-brand-cta group-hover:text-white transition-all duration-300 shadow-md">
                                        <IconComponent className="w-8 h-8" />
                                    </div>
                                    <div className="space-y-4 flex-1">
                                        <h2 className="text-2xl font-bold font-heading text-brand-text">
                                            {section.title}
                                        </h2>
                                        <p className="text-brand-text/80 font-body text-base leading-relaxed">
                                            {section.body}
                                        </p>
                                    </div>
                                </div>
                            </ScrollReveal>
                        );
                    })}
                </div>
            </div>
        </main>
    );
}
